/**
 * Frontend Logger Utility
 * 
 * A configurable logger that can be disabled in production environments.
 * Provides different log levels and environment-aware logging.
 */

// Configuration
const config = {
    // Check if we're in development mode
    isDevelopment: process.env.NODE_ENV === 'development',
    
    // Allow override via environment variable
    enableLogging: process.env.REACT_APP_ENABLE_LOGGING !== 'false',
    
    // Log levels
    levels: {
        ERROR: 0,
        WARN: 1,
        INFO: 2,
        DEBUG: 3
    },
    
    // Current log level (can be overridden)
    currentLevel: process.env.REACT_APP_LOG_LEVEL || 'DEBUG'
};

// Determine if logging should be enabled
const shouldLog = config.isDevelopment && config.enableLogging;

// Get numeric value for log level
const getLogLevelValue = (level) => {
    return config.levels[level.toUpperCase()] || config.levels.DEBUG;
};

// Check if a log level should be output
const shouldLogLevel = (level) => {
    if (!shouldLog) return false;
    
    const levelValue = getLogLevelValue(level);
    const currentLevelValue = getLogLevelValue(config.currentLevel);
    
    return levelValue <= currentLevelValue;
};

// Create a timestamp for logs
const getTimestamp = () => {
    return new Date().toISOString().replace('T', ' ').replace('Z', '');
};

// Format log message with prefix
const formatMessage = (level, message, ...args) => {
    const timestamp = getTimestamp();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
    
    if (typeof message === 'string') {
        return [`${prefix} ${message}`, ...args];
    } else {
        return [prefix, message, ...args];
    }
};

// Logger class
class Logger {
    /**
     * Error logging - always shown in development
     */
    error(message, ...args) {
        if (shouldLogLevel('ERROR')) {
            console.error(...formatMessage('ERROR', message, ...args));
        }
    }

    /**
     * Warning logging
     */
    warn(message, ...args) {
        if (shouldLogLevel('WARN')) {
            console.warn(...formatMessage('WARN', message, ...args));
        }
    }

    /**
     * Info logging
     */
    info(message, ...args) {
        if (shouldLogLevel('INFO')) {
            console.info(...formatMessage('INFO', message, ...args));
        }
    }

    /**
     * Debug logging
     */
    debug(message, ...args) {
        if (shouldLogLevel('DEBUG')) {
            console.log(...formatMessage('DEBUG', message, ...args));
        }
    }

    /**
     * Group logging - useful for organizing related logs
     */
    group(label) {
        if (shouldLog) {
            console.group(`[${getTimestamp()}] ${label}`);
        }
    }

    groupEnd() {
        if (shouldLog) {
            console.groupEnd();
        }
    }

    /**
     * Table logging - useful for displaying objects/arrays
     */
    table(data, columns) {
        if (shouldLog) {
            console.table(data, columns);
        }
    }

    /**
     * Time logging - useful for performance monitoring
     */
    time(label) {
        if (shouldLog) {
            console.time(`[TIMER] ${label}`);
        }
    }

    timeEnd(label) {
        if (shouldLog) {
            console.timeEnd(`[TIMER] ${label}`);
        }
    }

    /**
     * Conditional logging
     */
    assert(condition, message, ...args) {
        if (shouldLog) {
            console.assert(condition, ...formatMessage('ASSERT', message, ...args));
        }
    }

    /**
     * Get current configuration
     */
    getConfig() {
        return {
            isDevelopment: config.isDevelopment,
            enableLogging: config.enableLogging,
            shouldLog,
            currentLevel: config.currentLevel,
            levels: config.levels
        };
    }

    /**
     * Check if a specific log level is enabled
     */
    isLevelEnabled(level) {
        return shouldLogLevel(level);
    }
}

// Create and export logger instance
const logger = new Logger();

export default logger;

// Also export individual methods for convenience
export const { error, warn, info, debug, group, groupEnd, table, time, timeEnd, assert } = logger;

// Export configuration utilities
export const isLoggingEnabled = () => shouldLog;
export const getLogLevel = () => config.currentLevel;
export const setLogLevel = (level) => {
    if (config.levels[level.toUpperCase()] !== undefined) {
        config.currentLevel = level.toUpperCase();
    }
}; 